import React, { useState } from 'react';
import { format } from 'date-fns';
import { Sparkles, Loader2, AlertCircle } from 'lucide-react';

const ZODIAC_SIGNS = [
  { 
    name: 'Aries', 
    dates: 'March 21 - April 19',
    symbol: '♈'
  },
  { 
    name: 'Taurus', 
    dates: 'April 20 - May 20',
    symbol: '♉'
  },
  { 
    name: 'Gemini', 
    dates: 'May 21 - June 20',
    symbol: '♊'
  },
  { 
    name: 'Cancer', 
    dates: 'June 21 - July 22',
    symbol: '♋'
  },
  { 
    name: 'Leo', 
    dates: 'July 23 - August 22',
    symbol: '♌'
  },
  { 
    name: 'Virgo', 
    dates: 'August 23 - September 22',
    symbol: '♍'
  },
  { 
    name: 'Libra', 
    dates: 'September 23 - October 22',
    symbol: '♎'
  },
  { 
    name: 'Scorpio', 
    dates: 'October 23 - November 21',
    symbol: '♏'
  },
  { 
    name: 'Sagittarius', 
    dates: 'November 22 - December 21',
    symbol: '♐'
  },
  { 
    name: 'Capricorn', 
    dates: 'December 22 - January 19',
    symbol: '♑'
  },
  { 
    name: 'Aquarius', 
    dates: 'January 20 - February 18',
    symbol: '♒'
  },
  { 
    name: 'Pisces', 
    dates: 'February 19 - March 20',
    symbol: '♓'
  },
];

interface HoroscopeData {
  sign: string;
  date: string;
  horoscope: string;
}

export default function Horoscope() {
  const [selectedSign, setSelectedSign] = useState<string | null>(null);
  const [horoscope, setHoroscope] = useState<HoroscopeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  const handleSignClick = async (sign: string) => {
    setSelectedSign(sign);
    
    // Rate limiting: Only allow fetches every 2 seconds
    const now = Date.now();
    if (now - lastFetchTime < 2000) {
      return;
    }
    
    setLastFetchTime(now);
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/daily-horoscope`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ sign }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch horoscope');
      }

      const data = await response.json();
      setHoroscope(data);
    } catch (err: any) {
      console.error('Error fetching horoscope:', err);
      setError(err.message || 'Failed to fetch horoscope');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600">
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-8">
          <h1 className="text-4xl font-bold text-white mb-4">Daily Horoscopes</h1>
          <p className="text-purple-200 text-lg">
            {format(new Date(), 'MMMM d, yyyy')}
          </p>
        </div>

        <div className="flex gap-8 p-6">
          {/* Zodiac Signs Sidebar */}
          <div className="w-64 space-y-2">
            {ZODIAC_SIGNS.map((sign) => (
              <button
                key={sign.name}
                onClick={() => handleSignClick(sign.name)}
                className={`w-full flex items-center p-4 rounded-lg transition-all ${
                  selectedSign === sign.name
                    ? 'bg-white text-purple-900'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <span className="text-3xl mr-4 font-serif">{sign.symbol}</span>
                <div className="text-left">
                  <div className="font-semibold">{sign.name}</div>
                  <div className="text-xs opacity-75">{sign.dates}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {selectedSign ? (
              <div className="bg-white rounded-lg shadow-xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-bold flex items-center">
                    <Sparkles className="text-purple-600 w-8 h-8 mr-3" />
                    {selectedSign} {ZODIAC_SIGNS.find(s => s.name === selectedSign)?.symbol}
                  </h2>
                  <p className="text-gray-600">{format(new Date(), 'MMMM d, yyyy')}</p>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 rounded-lg flex items-center text-red-700">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    <p>{error}</p>
                  </div>
                )}

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                  </div>
                ) : horoscope ? (
                  <div className="prose max-w-none">
                    <p className="text-xl leading-relaxed text-gray-700">
                      {horoscope.horoscope}
                    </p>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-white/60">
                <p className="text-xl">Select a zodiac sign to view your horoscope</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}